import { getBasePackageUniqueKey } from 'lib/offer/offerUtils'

export interface FlashCancellationOptionRow {
  key: string
  policyType: App.CancellationPolicyType | undefined
  price: number
  priceDifference: number
  isSelected: boolean
  isBase: boolean
}

type GetViewPrice = (view: App.OfferPackageOptionView) => number

const getDisplayedPrice: GetViewPrice = view => view.pricing.price

export function isFlashCancellationOptionView(view: App.OfferPackageOptionView): boolean {
  return !!view.package.cancellationOptionPolicy
}

export function groupFlashCancellationOptionViews(
  views: Array<App.OfferPackageOptionView>,
): Map<string, Array<App.OfferPackageOptionView>> {
  const groups = new Map<string, Array<App.OfferPackageOptionView>>()
  for (const view of views) {
    const baseKey = getBasePackageUniqueKey(view.package)
    const group = groups.get(baseKey)
    if (group) {
      group.push(view)
    } else {
      groups.set(baseKey, [view])
    }
  }
  return groups
}

export function orderFlashCancellationOptionViews(
  views: Array<App.OfferPackageOptionView>,
  getPrice: GetViewPrice = getDisplayedPrice,
): Array<App.OfferPackageOptionView> {
  return [...views].sort((a, b) =>
    getPrice(a) - getPrice(b) || Number(isFlashCancellationOptionView(a)) - Number(isFlashCancellationOptionView(b)),
  )
}

export function getDefaultFlashCancellationOptionView(
  views: Array<App.OfferPackageOptionView>,
): App.OfferPackageOptionView | undefined {
  return views.find(view => !isFlashCancellationOptionView(view)) ?? views[0]
}

export function buildFlashCancellationOptionRows(
  views: Array<App.OfferPackageOptionView>,
  selectedKey: string | undefined,
  getPrice: GetViewPrice = getDisplayedPrice,
): Array<FlashCancellationOptionRow> {
  const sellable = orderFlashCancellationOptionViews(views.filter(view => view.hotelAvailable), getPrice)
  if (sellable.length < 2) {
    return []
  }

  const selected = sellable.find(view => view.package.uniqueKey === selectedKey) ?? getDefaultFlashCancellationOptionView(sellable)!
  const selectedPrice = getPrice(selected)

  return sellable.map((view) => {
    const isSelected = view === selected
    const price = getPrice(view)
    return {
      key: view.package.uniqueKey,
      policyType: view.package.roomRate?.cancellationPolicy?.type,
      price,
      priceDifference: isSelected ? 0 : price - selectedPrice,
      isSelected,
      isBase: !isFlashCancellationOptionView(view),
    }
  })
}

export function collapseFlashCancellationOptionViews(
  views: Array<App.OfferPackageOptionView>,
  choices: Record<string, App.CancellationPolicyType | undefined> = {},
): Array<App.OfferPackageOptionView> {
  if (!views.some(isFlashCancellationOptionView)) {
    return views
  }

  return [...groupFlashCancellationOptionViews(views)].map(([baseKey, group]) => {
    const policy = choices[baseKey]
    const chosen = policy ? group.find(option => option.package.cancellationOptionPolicy === policy && option.hotelAvailable) : undefined
    return chosen ?? getDefaultFlashCancellationOptionView(group)!
  })
}
